/**
 * Linked color swatches.
 *
 * Each color is its own product, so clicking a swatch has to repaint the whole host — image, title, color label,
 * price and every href — not just switch a variant. All of that data already rides on the swatch element, so this
 * never touches the network.
 *
 * ---------------------------------------------------------------------------------------------------------------
 * Hosts
 * ---------------------------------------------------------------------------------------------------------------
 * A "host" is whatever block the swatch repaints: a product card, or a PDP upsell item. Cards are found by their
 * own markup; anything else opts in with data-lcs-host and marks its parts with data-lcs="title|color|price|
 * image|image-secondary|link". The card selectors are kept as fallbacks so already-deployed card markup keeps
 * working without edits.
 *
 * ---------------------------------------------------------------------------------------------------------------
 * Why the swatches stop being links
 * ---------------------------------------------------------------------------------------------------------------
 * They render as real <a href> so they work without JS and are crawlable. As soon as this file runs, each swatch is
 * converted into a button: the href is removed (the destination already lives on data-url) and role/tabindex are
 * set. Relying on preventDefault alone was not enough — a link can be navigated by things that fire before click
 * (pointerdown handlers, prefetch/instant-navigation libraries, a stray theme listener).
 *
 * Inside the upsell the swatch is rendered as a <span role="button"> instead, because an <a> nested in the item's
 * wrapping <a> is invalid and makes the parser restructure the DOM. preventDefault still cancels that outer
 * anchor's navigation.
 *
 * Sets document.documentElement.dataset.linkedColorSwatches = 'ready' so you can confirm in DevTools that the file
 * actually loaded.
 */
(function () {
  'use strict';

  // The snippet emits one tag per host; the browser fetches the file once but executes it per tag.
  if (window.__linkedColorSwatchesInit) return;
  window.__linkedColorSwatchesInit = true;

  var SWATCH = '.linked-color-swatch';
  var HOST = '[data-lcs-host], product-card, .product-card';
  var DRAG_TOLERANCE = 6; // px of pointer travel before a click counts as a drag of the swatch slider

  // ------------------------------------------------------------------------------------------------------------
  // Link → button conversion
  // ------------------------------------------------------------------------------------------------------------

  function convert(swatch) {
    if (!swatch || !swatch.hasAttribute('href')) return;

    // data-url already carries the destination; keep a copy of the href in case the markup ever omits it.
    if (!swatch.dataset.url) swatch.dataset.url = swatch.getAttribute('href');

    swatch.removeAttribute('href');
    swatch.setAttribute('role', 'button');
    swatch.setAttribute('tabindex', '0');
  }

  function convertWithin(root) {
    (root || document).querySelectorAll(SWATCH + '[href]').forEach(convert);
  }

  function swatchFrom(event) {
    var node = event.target;
    if (!node || !node.closest) return null;
    return node.closest(SWATCH);
  }

  // ------------------------------------------------------------------------------------------------------------
  // Host repainting
  // ------------------------------------------------------------------------------------------------------------

  /**
   * Point an <img> at a new source. srcset/sizes must go, or the browser keeps picking a candidate from the OLD
   * descriptor set and the swap silently does nothing on high-DPR screens.
   */
  function setImage(img, src) {
    if (!img || !src) return;
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.setAttribute('src', src);
  }

  function setHref(el, url) {
    if (el && url) el.setAttribute('href', url);
  }

  /** Static figure: one primary image plus, optionally, one hover image. */
  function paintStaticFigure(figure, data) {
    if (!figure) return;
    setImage(figure.querySelector('.product-card__image--primary'), data.image);
    setImage(figure.querySelector('.product-card__image--secondary'), data.imageSecondary || data.image);
  }

  /**
   * Carousel figure: a Swiper built from the ORIGINAL product's full media list. Only the selected color's two
   * images are available, so slides 1 and 2 are repointed and any further slides are hidden — otherwise the
   * carousel would keep showing the previous color further along.
   */
  function paintCarouselFigure(figure, data) {
    if (!figure) return;

    var slides = figure.querySelectorAll('.product-card__image--primary');
    if (!slides.length) return;

    slides.forEach(function (slide, i) {
      if (i === 0) {
        slide.style.removeProperty('display');
        setImage(slide, data.image);
      } else if (i === 1 && data.imageSecondary) {
        slide.style.removeProperty('display');
        setImage(slide, data.imageSecondary);
      } else {
        slide.style.display = 'none';
      }
    });

    var swiperEl = figure.querySelector('.image_main_card');
    if (swiperEl && swiperEl.swiper) {
      swiperEl.swiper.slideTo(0, 0);
      swiperEl.swiper.update();
    }
  }

  function applyColor(swatch, host) {
    var data = swatch.dataset;

    // --- selected state -------------------------------------------------------------------------------------
    host.querySelectorAll(SWATCH).forEach(function (other) {
      var active = other === swatch;
      other.classList.toggle('is-selected', active);
      other.classList.toggle('is-active', active);
      if (active) {
        other.setAttribute('aria-current', 'true');
      } else {
        other.removeAttribute('aria-current');
      }
    });

    // --- title ----------------------------------------------------------------------------------------------
    if (data.title) {
      host.querySelectorAll('[data-lcs="title"]').forEach(function (el) {
        el.textContent = data.title;
      });
    }

    // Card titles are links, so they get the href too.
    host.querySelectorAll('.product-title').forEach(function (el) {
      if (data.title) el.textContent = data.title;
      setHref(el, data.url);
    });

    // --- color label ----------------------------------------------------------------------------------------
    if (data.colorName) {
      host.querySelectorAll('[data-lcs="color"], .var-colo-title').forEach(function (el) {
        // The upsell prints the raw color next to a "Color:" label; the card prints it uppercased.
        var upper = el.matches('.var-colo-title');
        el.textContent = upper ? data.colorName.toUpperCase() : data.colorName;
        el.setAttribute('data-title', data.colorName);
      });
    }

    // --- price ----------------------------------------------------------------------------------------------
    // Cloned from the swatch's inert <template> so compare-at and sale-event markup survives intact.
    var priceTemplate = swatch.querySelector('.linked-color-swatch__price');
    var priceSlot = host.querySelector('[data-lcs="price"], .js-card-price');
    if (priceTemplate && priceTemplate.content && priceSlot) {
      priceSlot.replaceChildren(priceTemplate.content.cloneNode(true));
    }

    // --- images ---------------------------------------------------------------------------------------------
    // A product card renders two figures, and the mobile one is a Swiper whose slides ALSO carry
    // .product-card__image--primary — so each is handled separately rather than with one unscoped query.
    var mobileFigure = host.querySelector('.product-card__figure.mobile-show');
    var desktopFigure = host.querySelector('.product-card__figure.desktop-show');

    paintCarouselFigure(mobileFigure, data);
    paintStaticFigure(desktopFigure, data);

    // Hosts with a single, unqualified figure (product-card.liquid).
    if (!mobileFigure && !desktopFigure) {
      paintStaticFigure(host.querySelector('.product-card__figure'), data);
    }

    // Plainly marked images (upsell item).
    host.querySelectorAll('[data-lcs="image"]').forEach(function (el) {
      setImage(el, data.image);
    });
    host.querySelectorAll('[data-lcs="image-secondary"]').forEach(function (el) {
      setImage(el, data.imageSecondary || data.image);
    });

    // --- links and buy targets ------------------------------------------------------------------------------
    host.querySelectorAll('[data-lcs="link"], a.product-card__media').forEach(function (el) {
      setHref(el, data.url);
    });

    if (data.handle) {
      var quickBuy = host.querySelector('quick-buy-modal');
      if (quickBuy) {
        quickBuy.setAttribute(
          'handle',
          data.variantId ? data.handle + '?variant=' + data.variantId : data.handle
        );
      }
    }

    // Add-to-cart targets: the hidden variant input, and the upsell button's own data attribute.
    if (data.variantId) {
      host.querySelectorAll('input[name="id"]').forEach(function (el) {
        el.value = data.variantId;
      });
      host.querySelectorAll('[data-variant_id]').forEach(function (el) {
        el.setAttribute('data-variant_id', data.variantId);
      });
    }

    host.classList.toggle('is-color-sold-out', data.available === 'false');
  }

  function select(swatch) {
    var host = swatch.closest(HOST);
    if (!host) return;

    try {
      applyColor(swatch, host);
    } catch (error) {
      console.error('[linked-color-swatches] failed to update host', error);
    }
  }

  // ------------------------------------------------------------------------------------------------------------
  // Events
  // ------------------------------------------------------------------------------------------------------------

  // The swatch row is draggable. Without this, finishing a drag on top of a swatch would select that color.
  var pointerStart = null;

  document.addEventListener(
    'pointerdown',
    function (event) {
      pointerStart = { x: event.clientX, y: event.clientY };

      // Convert before the click lands, so hosts rendered after page load can never navigate either.
      var swatch = swatchFrom(event);
      if (swatch) convert(swatch);
    },
    true
  );

  document.addEventListener(
    'focusin',
    function (event) {
      var swatch = swatchFrom(event);
      if (swatch) convert(swatch);
    },
    true
  );

  function wasDragged(event) {
    if (!pointerStart) return false;
    return (
      Math.abs(event.clientX - pointerStart.x) > DRAG_TOLERANCE ||
      Math.abs(event.clientY - pointerStart.y) > DRAG_TOLERANCE
    );
  }

  document.addEventListener(
    'click',
    function (event) {
      var swatch = swatchFrom(event);
      if (!swatch) return;

      // Cancels the swatch itself and, in the upsell, the <a> wrapping the whole item.
      event.preventDefault();
      event.stopPropagation();

      if (wasDragged(event)) return;

      select(swatch);
    },
    true // capture
  );

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    var swatch = swatchFrom(event);
    if (!swatch) return;

    event.preventDefault();
    select(swatch);
  });

  // ------------------------------------------------------------------------------------------------------------
  // Init
  // ------------------------------------------------------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      convertWithin(document);
    });
  } else {
    convertWithin(document);
  }

  document.documentElement.dataset.linkedColorSwatches = 'ready';
})();